import { Paper, Text, Stack, Progress, Button, Box, Divider } from '@mantine/core';
import { useContext } from 'react';
import AuthContext from 'context/AuthContext';
import { AccessLevel } from 'interfaces/GetUserResult';
import { useNavigate } from 'react-router-dom';

interface ActionItemsProps {
  teamMemberCount: number;
  teamMemberLimit: number;
  characterCount: number;
  characterLimit: number;
}

const WARNING_THRESHOLD = 70;

const getProgressColor = (percentage: number) => {
  if (percentage >= 100) return 'var(--red-color)';
  if (percentage >= WARNING_THRESHOLD) return 'var(--yellow-color)';
  return 'var(--green-color)';
};

export function ActionItems({ teamMemberCount, teamMemberLimit, characterCount, characterLimit }: ActionItemsProps) {
  const navigate = useNavigate();
  const { data: { account } } = useContext(AuthContext);

  // Calculate progress percentages
  const teamMemberProgress = (teamMemberCount / teamMemberLimit) * 100;
  const characterProgress = (characterCount / characterLimit) * 100;

  // Show action items section when either progress is above threshold and user is on free plan
  const showActionItems = (teamMemberProgress >= WARNING_THRESHOLD || characterProgress >= WARNING_THRESHOLD) && account?.access_level === AccessLevel.FREE;

  // Show upgrade cards for free users
  const showUpgradeCards = account?.access_level === AccessLevel.FREE;

  const handleUpgradeClick = () => {
    navigate('/upgrade');
  };

  if (!showUpgradeCards) {
    return null;
  }

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        {/* Conditional Warnings Section */}
        {showActionItems && (
          <>
            {teamMemberProgress >= WARNING_THRESHOLD && (
                <Box>
                  <Text fw={600} mb="xs">You're almost at your team member limit!</Text>
          <Text size="sm" c="dimmed" mb="xs">
            You currently have {teamMemberCount} of {teamMemberLimit} available team member slots in use. 
            As your business grows, upgrade to add up to 100 team members.
          </Text>
          <Progress 
            value={teamMemberProgress} 
                    size="md"
                    radius="xl"
                    color={getProgressColor(teamMemberProgress)}
                    styles={{
                      root: {
                        backgroundColor: 'var(--mantine-color-gray-2)'
                      },
                      section: {
                        backgroundColor: getProgressColor(teamMemberProgress)
                      }
                    }}
          />
                </Box>
            )}

            {characterProgress >= WARNING_THRESHOLD && (
              <Box mt={teamMemberProgress >= WARNING_THRESHOLD ? "md" : 0}>
                <Text fw={600} mb="xs">You're approaching your Business Information character count limit</Text>
          <Text size="sm" c="dimmed" mb="xs">
                  Total characters used:
          </Text>
          <Progress 
            value={characterProgress} 
                  size="md"
                  radius="xl"
                  color={getProgressColor(characterProgress)}
                  mb="xs"
                  styles={{
                    root: {
                      backgroundColor: 'var(--mantine-color-gray-2)'
                    },
                    section: {
                      backgroundColor: getProgressColor(characterProgress)
                    }
                  }}
          />
                <Text fw={600} size="sm" ta="right">{characterCount}/{characterLimit}</Text>
          <Text size="sm" c="dimmed" mt="xs">
            Upgrade your plan to keep enhancing your AI receptionist's capabilities.
          </Text>
              </Box>
            )}

            {(teamMemberProgress >= WARNING_THRESHOLD || characterProgress >= WARNING_THRESHOLD) && (
              <Divider my="md" />
            )}
          </>
      )}

        {/* Static Upgrade Features - Always shown for free users */}
            <Box>
              <Text fw={600} mb="xs">Get instant missed call alerts on your phone.</Text>
              <Text size="sm" c="dimmed">
            Unlock SMS alerts for missed calls so you can respond faster and stay connected wherever you are.
          </Text>
            </Box>

            <Divider my="sm" />

            <Box>
              <Text fw={600} mb="xs">More than just call logs - get the full picture.</Text>
              <Text size="sm" c="dimmed">
            Access your full call detail history: recordings, transcripts, and advanced filters to track, analyze, and optimize every conversation.
          </Text>
            </Box>

            <Button 
              variant="filled" 
              color="green" 
              fullWidth
              size="md"
              mt="xs"
              onClick={handleUpgradeClick}
              styles={{
                root: {
                  backgroundColor: 'var(--green-color)',
                  '&:hover': {
                    backgroundColor: 'var(--green-color)',
                    opacity: 0.9
                  }
                }
              }}
            >
            Upgrade now
          </Button>
      </Stack>
    </Paper>
  );
} 